import { Injectable } from '@angular/core';
import { Plani8EnvironmentService, TUploadResponseDTO } from '@monorepo/services';
import { ModalController } from '@ionic/angular';
import { ConfirmationModalComponent, DefaultInfoModalComponent, TConfirmationModalDimissEmitVal } from '@monorepo/ui';
import { UploadModalComponent, UploadSummaryModalComponent } from '../app/components/plani8-common';
import { EXCEL_CONTEXT, TExcelManagementContext, TExcelManagementInfo } from '../types';

type TFilterSearchBoardType = 'LL_BOARD' | 'TNA_BOARD' | 'HL_BOARD' | 'OPL_DAILY_VIEW' | 'PLANNING_BOARD';

@Injectable({ providedIn: 'root' })
export class ListingHelperClass {
  constructor(private modalCtrl: ModalController, private plani8EnvironmentService: Plani8EnvironmentService) {}

  async openDeleteConfirmationModal(header: string) {
    const editConfirmationModal = await this.modalCtrl.create({
      component: ConfirmationModalComponent,
      animated: false,
      backdropDismiss: false,
      keyboardClose: false,
      componentProps: {
        header,
        closeModalBtnTxt: 'Cancel',
        primaryBtnTxt: 'Yes',
      },
      cssClass: 'm-modal__delete-modal',
    });

    await editConfirmationModal.present();
    const dismissEmitValue = (await editConfirmationModal.onWillDismiss()) as TConfirmationModalDimissEmitVal;

    switch (dismissEmitValue.role) {
      case 'SAVE':
        return true;
      case 'CLOSE':
        return false;
    }
  }

  async openUploadModal(excelInfo: TExcelManagementInfo) {
    const uploadModal = await this.modalCtrl.create({
      component: UploadModalComponent,
      componentProps: { excelInfo },
      cssClass: 'plani8-upload-modal',
    });

    await uploadModal.present();
  }

  async openUploadSummaryModal(
    summary: TUploadResponseDTO,
    excelInfo: TExcelManagementInfo,
  ): Promise<HTMLIonModalElement> {
    if (excelInfo.hasExcelErrorTemplate) {
      const uploadSummaryModal = await this.modalCtrl.create({
        component: UploadSummaryModalComponent,
        componentProps: { summary, excelInfo },
        cssClass: 'plani8-upload-summary-modal',
      });
      await uploadSummaryModal.present();
      return uploadSummaryModal;
    } else {
      const uploadSuccessModal = await this.modalCtrl.create({
        component: DefaultInfoModalComponent,
        componentProps: {
          infoHeader: 'Success!',
          infoTxt: 'Successfully uploaded the excel file.',
        },
        cssClass: 'plani8-upload-summary-modal',
      });
      await uploadSuccessModal.present();
      return uploadSuccessModal;
    }
  }

  getFileUploadUrl(excelInfo: TExcelManagementInfo) {
    switch (excelInfo.excelContext) {
      case EXCEL_CONTEXT.PRODUCED_QTY:
        return this.plani8EnvironmentService.getUrlFromEnv('PRODUCED_QTY_UPLOAD_XLSX');
      case EXCEL_CONTEXT.ORDERS_UPLOAD:
        return this.plani8EnvironmentService.getUrlFromEnv('ORDER_LIST_UPLOAD_XLSX');
      case EXCEL_CONTEXT.MACHINE_AVAILABILITY:
        return this.plani8EnvironmentService
          .getUrlFromEnv('MACHINE_AVAILABILITY_UPLOAD')
          .replace(':factoryId', excelInfo.factoryId);
      case EXCEL_CONTEXT.OPERATION_BREAKDOWN:
        return this.plani8EnvironmentService
          .getUrlFromEnv('OPERATION_BREAKDOWN_UPLOAD')
          .replace(':otlId', excelInfo.otlId)
          .replace(':productType', excelInfo.productType);
      case EXCEL_CONTEXT.MATERIAL_INHOUSE:
        return this.plani8EnvironmentService.getUrlFromEnv('MATERIAL_INHOUSE_UPLOAD');
    }
  }

  getDownloadFileUrl(excelInfo: TExcelManagementInfo) {
    switch (excelInfo.excelContext) {
      case EXCEL_CONTEXT.PRODUCED_QTY:
        return this.plani8EnvironmentService.getUrlFromEnv('DOWNLOAD_PO_CARD_SAMPLE_DETAILS');
      case EXCEL_CONTEXT.ORDERS_UPLOAD:
        return this.plani8EnvironmentService.getUrlFromEnv('DOWNLOAD_SAMPLE_ORDERS_TEMPLATE');
      case EXCEL_CONTEXT.MACHINE_AVAILABILITY:
        return this.plani8EnvironmentService.getUrlFromEnv('MACHINE_AVAILABILITY_TEMPLATE_DOWNLOAD');
      case EXCEL_CONTEXT.OPERATION_BREAKDOWN:
        return this.plani8EnvironmentService.getUrlFromEnv('OPERATION_BREAKDOWN_TEMPLATE_DOWNLOAD');
      case EXCEL_CONTEXT.MATERIAL_INHOUSE:
        return this.plani8EnvironmentService.getUrlFromEnv('MATERIAL_INHOUSE_TEMPLATE_DOWNLOAD');
    }
  }

  getDownloadErrorTemplateUrl(excelContext: TExcelManagementContext, errorKey: string) {
    switch (excelContext) {
      case EXCEL_CONTEXT.PRODUCED_QTY:
        return this.plani8EnvironmentService.getUrlFromEnv('PRODUCED_QTY_EXCEL_ERROR_SHEET').replace(':key', errorKey);
      case EXCEL_CONTEXT.ORDERS_UPLOAD:
        return this.plani8EnvironmentService
          .getUrlFromEnv('ORDER_LIST_DOWNLOAD_EXCEL_ERROR_SHEET')
          .replace(':key', errorKey);
      case EXCEL_CONTEXT.MATERIAL_INHOUSE:
        return this.plani8EnvironmentService
          .getUrlFromEnv('MATERIAL_INHOUSE_EXCEL_ERROR_SHEET')
          .replace(':key', errorKey);
      default:
        return '';
    }
  }

  getFilterSearchEndPoint(whichType: TFilterSearchBoardType) {
    switch (whichType) {
      case 'LL_BOARD':
        return this.plani8EnvironmentService.getUrlFromEnv('LL_FILTERS_SEARCH_API');
      case 'TNA_BOARD':
        return this.plani8EnvironmentService.getUrlFromEnv('GET_TNA_BOARD_FILTERS_SEARCH');
      case 'HL_BOARD':
        return this.plani8EnvironmentService.getUrlFromEnv('HL_BOARD_FILTERS_SEARCH_API');
      case 'OPL_DAILY_VIEW':
        return this.plani8EnvironmentService.getUrlFromEnv('HL_OTL_FILTERS_SEARCH_API');
      case 'PLANNING_BOARD':
        return this.plani8EnvironmentService.getUrlFromEnv('PLANNING_BOARD_FILTERS_SEARCH_API');
    }
  }
}
